/**
 * longeva-menu-polish.js — premium menu micro-interactions.
 * Mobile drawer: staggered reveal, submenu scroll-snap, brass tap-line.
 * Desktop mega-menu: column fade-in stagger, cursor-trailing brass
 * indicator. Respects prefers-reduced-motion. AbortController cleanup.
 *
 * Horizon Alignment Audit (2026-08-08): removed initHoverIntent() (set an
 * attribute nothing read), initStickyShowHide() (duplicated header.js's own
 * throttled data-scroll-direction/data-sticky-state computation with an
 * un-throttled listener), and the entire disabled Shop-by-Brand A-Z
 * pill-bar rebuild (initBrandPillBar/initBrandPillBarMobile/
 * watchForBrandMenuRender) — both early-returned since 2026-05-14/16 and
 * confirmed to have zero live effect; watchForBrandMenuRender still ran a
 * sitewide MutationObserver for no benefit.
 *
 * Workstream 1 (2026-08-09): removed initDrawerBodyLock(). The mobile
 * drawer's own <details scroll-lock> already opts into Horizon's native
 * body-scroll-lock (assets/dialog.js's document-level `toggle` listener
 * sets html[scroll-lock] automatically on open/close) -- this function
 * duplicated that with a differently-named body class, driven by a
 * MutationObserver instead of the native toggle event, and its cleanup
 * was gated behind the same visibilitychange/AbortController pattern that
 * permanently disabled it after any tab-backgrounding (the root cause of
 * one of the two diagnosed mobile-navigation defects). The two real CSS
 * consumers of the old .longeva-drawer-open class have been rebound
 * directly to the drawer's own native `open` attribute via :has() --
 * see longeva-atmosphere-v2.css and longeva-atmosphere-deferred.css.
 * See CLAUDE.md.
 */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let rootAC = new AbortController();
  const sig = () => ({ signal: rootAC.signal });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      rootAC.abort();
      rootAC = new AbortController();
    }
  });

  function initStaggeredReveal() {
    if (reduced) return;
    const details = document.querySelector('#Details-menu-drawer-container');
    if (!details) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'class' || !details.classList.contains('menu-open')) continue;
        requestAnimationFrame(() => {
          details.querySelectorAll('.menu-drawer__animated-element').forEach((el, i) => {
            if (el instanceof HTMLElement) el.style.animationDelay = `${i * 40}ms`;
          });
        });
      }
    });
    mo.observe(details, { attributes: true, attributeFilter: ['class'] });
    rootAC.signal.addEventListener('abort', () => mo.disconnect());
  }

  function initSubmenuScrollSnap() {
    if (reduced) return;
    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Element)) return;
      const summary = e.target.closest('summary[on\\:click="header-drawer/open/submenu"]');
      if (!summary) return;
      const details = summary.closest('details');
      if (!details) return;
      setTimeout(() => {
        requestAnimationFrame(() => {
          const btn = details.querySelector('.menu-drawer__back-button');
          if (btn instanceof HTMLElement) btn.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }, 50);
    }, sig());
  }

  function initBrassLineTap() {
    if (reduced) return;
    document.addEventListener('pointerdown', (e) => {
      if (!(e.target instanceof Element)) return;
      const item = e.target.closest('.menu-drawer__menu-item');
      if (!(item instanceof HTMLElement) || item.querySelector('.lmp-tap-line')) return;
      const prevPos = getComputedStyle(item).position;
      if (prevPos === 'static') item.style.position = 'relative';
      const line = Object.assign(document.createElement('span'), { className: 'lmp-tap-line' });
      line.setAttribute('aria-hidden', 'true');
      line.style.cssText = 'position:absolute;bottom:0;left:0;width:100%;height:1px;background:#B99157;transform:scaleX(0);transform-origin:left center;pointer-events:none;will-change:transform,opacity';
      item.appendChild(line);
      requestAnimationFrame(() => {
        line.animate(
          [{ transform: 'scaleX(0)', opacity: 1 }, { transform: 'scaleX(1)', opacity: 1 }],
          { duration: 180, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' }
        ).finished.then(() =>
          line.animate(
            [{ transform: 'scaleX(1)', transformOrigin: 'right center', opacity: 1 }, { transform: 'scaleX(0)', transformOrigin: 'right center', opacity: 0 }],
            { duration: 180, easing: 'cubic-bezier(0.4,0,0.2,1)', fill: 'forwards' }
          ).finished
        ).then(() => {
          line.remove();
          if (prevPos === 'static') item.style.removeProperty('position');
        });
      });
    }, sig());
  }

  function initMegaMenuFadeIn() {
    if (reduced) return;
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'aria-expanded' || !(m.target instanceof HTMLElement)) continue;
        if (m.target.ariaExpanded !== 'true') continue;
        const submenu = m.target.parentElement?.querySelector('[ref="submenu[]"]');
        if (!(submenu instanceof HTMLElement)) continue;
        requestAnimationFrame(() => {
          submenu.querySelectorAll('.mega-menu__link, .mega-menu__content-list-item').forEach((el, i) => {
            if (el instanceof HTMLElement) {
              el.animate(
                [{ opacity: '0', transform: 'translateY(6px)' }, { opacity: '1', transform: 'translateY(0)' }],
                { duration: 200, delay: i * 30, easing: 'ease-out', fill: 'backwards' }
              );
            }
          });
        });
      }
    });
    headerMenu.querySelectorAll('[ref="menuitem"]').forEach((el) =>
      mo.observe(el, { attributes: true, attributeFilter: ['aria-expanded'] })
    );
    rootAC.signal.addEventListener('abort', () => mo.disconnect());
  }

  function initBrassIndicator() {
    if (reduced) return;
    const headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return;
    const ind = Object.assign(document.createElement('span'), { className: 'lmp-brass-indicator' });
    ind.setAttribute('aria-hidden', 'true');
    ind.style.cssText = 'position:fixed;height:0.5px;background:#B99157;pointer-events:none;transition:left 180ms cubic-bezier(0.4,0,0.2,1),width 180ms cubic-bezier(0.4,0,0.2,1),top 180ms cubic-bezier(0.4,0,0.2,1),opacity 140ms ease;opacity:0;z-index:9999;will-change:left,width,opacity';
    document.body.appendChild(ind);

    function show(listItem) {
      const link = listItem.querySelector('[ref="menuitem"]');
      if (!(link instanceof HTMLElement)) return;
      const r = link.getBoundingClientRect();
      ind.style.top = `${r.bottom - 0.5}px`;
      ind.style.left = `${r.left}px`;
      ind.style.width = `${r.width}px`;
      ind.style.opacity = '1';
    }
    function hide() { ind.style.opacity = '0'; }

    headerMenu.addEventListener('pointerenter', (e) => {
      if (!(e.target instanceof Element)) return;
      const li = e.target.closest('.menu-list__list-item');
      if (li instanceof HTMLElement) requestAnimationFrame(() => show(li));
    }, { capture: true, ...sig() });

    headerMenu.addEventListener('pointerleave', hide, sig());

    const closeMO = new MutationObserver(() => { if (headerMenu.ariaExpanded !== 'true') hide(); });
    closeMO.observe(headerMenu, { attributes: true, attributeFilter: ['aria-expanded'] });
    rootAC.signal.addEventListener('abort', () => { closeMO.disconnect(); ind.remove(); });
  }

  function boot() {
    initStaggeredReveal();
    initSubmenuScrollSnap();
    initBrassLineTap();
    initMegaMenuFadeIn();
    initBrassIndicator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
